/**
 * DecklineNameInput component
 * Renders the deckline name input field(s) based on the deckline type.
 * - Standard mode: single "Deckline Name" field
 * - Linked KPI mode (BASE_KPI): Autocomplete for Root KPI selection + Autocomplete for Variant
 */

import './DecklineNameInput.scss';

import { useAurigaContext } from '@context/AurigaContext';
import { useKPILibraryContext } from '@context/KPILibraryContext';
import { usePageContext } from '@context/PageContext';
import ContentPasteSearchIcon from '@mui/icons-material/ContentPasteSearch';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useGetVariantsForRootKPI } from '@services/api/deckManagement/hooks/useKPIVariants';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DecklineNameInputProps, RootKPIOption, VariantOption } from 'types/DecklineNameInput.types';
import { PageSidePanelDropDown } from 'types/PageSidePanel.types';

const rootKpiFilter = createFilterOptions<RootKPIOption>();
const variantFilter = createFilterOptions<VariantOption>();

const BASE_CLASS_NAME = 'deckline-name-input';

const DecklineNameInput = ({
  value,
  rootKpiId = null,
  kpiVariantId = null,
  decklineType,
  onNameChange,
  onLinkedKpiChange,
  onOpenKPILibrary,
  newVariantDataset,
  disabled = false,
}: DecklineNameInputProps) => {
  const isLinkedKpiMode = decklineType === PageSidePanelDropDown.BASE_KPI;

  // Datasets the page is configured for - existing Root KPIs/Variants are filtered to these (see below).
  const { configuredDatasets } = usePageContext();

  /** Datasets the page supports, as a set for membership tests. Empty ⇒ no filtering (see below). */
  const configuredDatasetSet = useMemo(() => new Set(configuredDatasets), [configuredDatasets]);

  const { featureFlags } = useAurigaContext();
  const enableKPICreation = featureFlags.enableKPILibraryCreate;

  const { pendingCreations, isLoading: isRootKPIsLoading } = useKPILibraryContext();
  const {
    allRootKPIs,
    pendingVariants: allPendingVariants,
    pendingRootKPIs,
    addPendingRootKPI,
    addPendingVariant,
    getEnhancedVariants,
  } = pendingCreations;

  /** Tracks the selected Root KPI ID for fetching child variants */
  const [localSelectedRootKpiId, setLocalSelectedRootKpiId] = useState<string | null>(rootKpiId);

  /** Sync internal state when the selectedDeckline changes (rootKpiId prop updates) */
  useEffect(() => {
    setLocalSelectedRootKpiId(rootKpiId);
  }, [rootKpiId]);

  /**
   * Root KPI options for the Autocomplete.
   *
   * Existing Root KPIs are shown only when at least one of their variants targets a dataset the page is
   * configured for — a Root KPI whose variants live entirely outside the page's `page_drivers` can't be
   * linked here. The currently-linked Root KPI is always kept, so an already-saved selection (e.g. a
   * deckline linked before the page's datasets changed) is never silently blanked. Pending (new) Root KPIs
   * are always shown; their variant creation is dataset-gated upstream. With no configured datasets we skip
   * filtering (linked-KPI mode is itself gated by page config upstream, so this only guards against an empty
   * transient state blanking the list).
   */
  const rootKpiOptions = useMemo(
    (): RootKPIOption[] =>
      allRootKPIs
        .filter((kpi) => {
          if (kpi.root_kpi_id === localSelectedRootKpiId) return true; // never drop the saved selection
          const isNew = pendingRootKPIs.some((p) => p.root_kpi_id === kpi.root_kpi_id);
          if (isNew || configuredDatasetSet.size === 0) return true;
          return (kpi.variants ?? []).some(
            (variant) => variant?.dataset_source != null && configuredDatasetSet.has(variant.dataset_source),
          );
        })
        .map((kpi) => ({
          root_kpi_id: kpi.root_kpi_id,
          root_kpi_name: kpi.root_kpi_name,
          isNew: pendingRootKPIs.some((p) => p.root_kpi_id === kpi.root_kpi_id),
        }))
        .sort((a, b) => a.root_kpi_name.localeCompare(b.root_kpi_name)),
    [allRootKPIs, pendingRootKPIs, configuredDatasetSet, localSelectedRootKpiId],
  );

  const { data: variants = [], isFetching: isVariantsFetching } = useGetVariantsForRootKPI({
    root_kpi_id: localSelectedRootKpiId ?? '',
    options: { enabled: !!localSelectedRootKpiId },
  });

  /**
   * Merges existing variants with pending variants for the selected Root KPI, sorted by name.
   *
   * Existing variants are shown only when their `dataset_source` is one the page is configured for; the
   * currently-linked variant is always kept so an already-saved selection isn't silently blanked. Pending
   * (new) variants are always shown. With no configured datasets we skip filtering (see rootKpiOptions).
   */
  const variantOptions = useMemo((): VariantOption[] => {
    const existing: VariantOption[] = variants
      .filter(
        (v) =>
          v.kpi_variant_id === kpiVariantId || // never drop the saved selection
          configuredDatasetSet.size === 0 ||
          (v.dataset_source != null && configuredDatasetSet.has(v.dataset_source)),
      )
      .map((v) => ({
        kpi_variant_id: v.kpi_variant_id,
        variant_name: v.variant_name,
      }));
    const pending: VariantOption[] = allPendingVariants
      .filter((p) => p.root_kpi_id === localSelectedRootKpiId)
      .map((p) => ({ kpi_variant_id: p.kpi_variant_id, variant_name: p.name, isNew: true }));
    return [...existing, ...pending].sort((a, b) => a.variant_name.localeCompare(b.variant_name));
  }, [variants, allPendingVariants, localSelectedRootKpiId, configuredDatasetSet, kpiVariantId]);

  /**
   * Names of all variants that already exist for the selected Root KPI (fetched + pending), independent of
   * dataset. Creation/existence checks must use this rather than the dataset-filtered `variantOptions`, or
   * typing the exact name of an off-dataset variant would spawn a duplicate pending creation.
   */
  const knownVariantNames = useMemo(() => {
    const names = new Set(variants.map((v) => v.variant_name));
    allPendingVariants.filter((p) => p.root_kpi_id === localSelectedRootKpiId).forEach((p) => names.add(p.name));
    return names;
  }, [variants, allPendingVariants, localSelectedRootKpiId]);

  // TODO: Root KPI list may be incomplete until getRootKPIs API supports nextToken pagination input.
  /** The currently selected Root KPI option, derived from selectedRootKpiId state */
  const selectedRootKpi = useMemo((): RootKPIOption | null => {
    if (!localSelectedRootKpiId) return null;
    return rootKpiOptions.find((kpi) => kpi.root_kpi_id === localSelectedRootKpiId) ?? null;
  }, [rootKpiOptions, localSelectedRootKpiId]);

  /** Controlled input value for the Root KPI Autocomplete */
  const [rootInputValue, setRootInputValue] = useState('');

  /** Sync input text when the resolved Root KPI changes */
  useEffect(() => {
    setRootInputValue(selectedRootKpi?.root_kpi_name ?? '');
  }, [selectedRootKpi]);

  /** The currently selected variant option, derived from the kpiVariantId prop */
  const selectedVariantValue = useMemo((): VariantOption | null => {
    if (!kpiVariantId) return null;
    if (isVariantsFetching) return null;
    return variantOptions.find((v) => v.kpi_variant_id === kpiVariantId) ?? null;
  }, [variantOptions, kpiVariantId, isVariantsFetching]);

  /** Controlled input value for the Variant Autocomplete */
  const [variantInputValue, setVariantInputValue] = useState('');

  /** Sync input text when the resolved Variant changes */
  useEffect(() => {
    setVariantInputValue(selectedVariantValue?.variant_name ?? '');
  }, [selectedVariantValue]);

  /** Handles selection of a Root KPI from the Autocomplete */
  const handleRootKpiSelect = useCallback(
    (_event: React.SyntheticEvent, selected: RootKPIOption | string | null) => {
      if (typeof selected === 'string') {
        if (!enableKPICreation) return;
        const trimmed = selected.trim();
        const newId = addPendingRootKPI(trimmed);
        setLocalSelectedRootKpiId(newId);
        onLinkedKpiChange?.({ id: newId, name: trimmed }, null);
        setRootInputValue(trimmed);
      } else if (selected?.isNew) {
        if (!enableKPICreation) return;
        const newId = addPendingRootKPI(selected.root_kpi_name);
        setLocalSelectedRootKpiId(newId);
        onLinkedKpiChange?.({ id: newId, name: selected.root_kpi_name }, null);
        setRootInputValue(selected.root_kpi_name);
      } else {
        setLocalSelectedRootKpiId(selected?.root_kpi_id ?? null);
        onLinkedKpiChange?.({ id: selected?.root_kpi_id ?? null, name: selected?.root_kpi_name ?? null }, null);
        setRootInputValue(selected?.root_kpi_name ?? '');
      }
    },
    [onLinkedKpiChange, addPendingRootKPI, enableKPICreation],
  );

  /** Handles selection of a Variant from the Autocomplete */
  const handleVariantSelect = useCallback(
    (_event: React.SyntheticEvent, selected: VariantOption | string | null) => {
      if (!localSelectedRootKpiId) return;
      const rootKpiPayload = { id: localSelectedRootKpiId, name: selectedRootKpi?.root_kpi_name ?? null };
      const enhancedList = getEnhancedVariants(localSelectedRootKpiId, variants);

      if (typeof selected === 'string') {
        if (!enableKPICreation) return;
        const enhanced = addPendingVariant(localSelectedRootKpiId, selected, newVariantDataset);
        onLinkedKpiChange?.(rootKpiPayload, enhanced);
      } else if (selected?.isNew) {
        if (!enableKPICreation) return;
        const enhanced = addPendingVariant(localSelectedRootKpiId, selected.variant_name, newVariantDataset);
        onLinkedKpiChange?.(rootKpiPayload, enhanced);
      } else {
        const enhanced = enhancedList.find((v) => v.kpi_variant_id === selected?.kpi_variant_id) ?? null;
        onLinkedKpiChange?.(rootKpiPayload, enhanced);
      }
    },
    [
      onLinkedKpiChange,
      localSelectedRootKpiId,
      selectedRootKpi,
      addPendingVariant,
      enableKPICreation,
      getEnhancedVariants,
      variants,
      newVariantDataset,
    ],
  );

  /** Filters Root KPI options, prepending a [CREATE] entry when input doesn't match existing */
  const filterRootKpiOptions = useCallback(
    (options: RootKPIOption[], params: { inputValue: string; getOptionLabel: (option: RootKPIOption) => string }) => {
      const trimmed = params.inputValue.trim();
      const filtered = rootKpiFilter(options, { ...params, inputValue: trimmed });
      if (enableKPICreation && trimmed && !allRootKPIs.some((kpi) => kpi.root_kpi_name === trimmed)) {
        filtered.unshift({ root_kpi_id: null, root_kpi_name: trimmed, isNew: true });
      }
      return filtered;
    },
    [allRootKPIs, enableKPICreation],
  );

  /** Filters variant options, prepending a [CREATE] entry when input doesn't match existing */
  const filterVariantOptions = useCallback(
    (options: VariantOption[], params: { inputValue: string; getOptionLabel: (option: VariantOption) => string }) => {
      const trimmed = params.inputValue.trim();
      const filtered = variantFilter(options, { ...params, inputValue: trimmed });
      if (enableKPICreation && trimmed && !knownVariantNames.has(trimmed)) {
        filtered.unshift({ kpi_variant_id: null, variant_name: trimmed, isNew: true });
      }
      return filtered;
    },
    [knownVariantNames, enableKPICreation],
  );

  /** On blur, resolve unmatched input: create if enabled, otherwise clear */
  const handleRootKpiBlur = useCallback(() => {
    const trimmed = rootInputValue.trim();
    if (!trimmed) return;

    // Match against the unfiltered list: an existing (but off-dataset, filtered-out) Root KPI must not
    // trigger a duplicate creation.
    const matchesOption = allRootKPIs.some((kpi) => kpi.root_kpi_name === trimmed);
    if (matchesOption) return;

    if (enableKPICreation) {
      const newId = addPendingRootKPI(trimmed);
      setLocalSelectedRootKpiId(newId);
      onLinkedKpiChange?.({ id: newId, name: trimmed }, null);
      setRootInputValue(trimmed);
    } else {
      // Clear to last valid selection
      setRootInputValue(selectedRootKpi?.root_kpi_name ?? '');
    }
  }, [rootInputValue, allRootKPIs, enableKPICreation, addPendingRootKPI, onLinkedKpiChange, selectedRootKpi]);

  /** On blur, resolve unmatched variant input: create if enabled, otherwise clear */
  const handleVariantBlur = useCallback(() => {
    const trimmed = variantInputValue.trim();
    if (!trimmed || !localSelectedRootKpiId) return;

    // Match against all known variant names (unfiltered): an existing off-dataset variant must not
    // trigger a duplicate creation.
    const matchesOption = knownVariantNames.has(trimmed);
    if (matchesOption) return;

    if (enableKPICreation) {
      const enhanced = addPendingVariant(localSelectedRootKpiId, trimmed, newVariantDataset);
      onLinkedKpiChange?.({ id: localSelectedRootKpiId, name: selectedRootKpi?.root_kpi_name ?? null }, enhanced);
      setVariantInputValue(trimmed);
    } else {
      setVariantInputValue(selectedVariantValue?.variant_name ?? '');
    }
  }, [
    variantInputValue,
    knownVariantNames,
    enableKPICreation,
    addPendingVariant,
    localSelectedRootKpiId,
    selectedRootKpi,
    onLinkedKpiChange,
    selectedVariantValue,
    newVariantDataset,
  ]);

  if (!isLinkedKpiMode) {
    return (
      <Box className={`${BASE_CLASS_NAME}__standard`}>
        <TextField
          fullWidth
          value={value}
          onChange={(e) => onNameChange?.(e.target.value)}
          label='Deckline Name'
          margin='dense'
          autoComplete='off'
          size='small'
          disabled={disabled}
        />
      </Box>
    );
  }

  return (
    <Box className={`${BASE_CLASS_NAME}__linked-kpi`}>
      <Stack spacing={1} useFlexGap direction='row' className={`${BASE_CLASS_NAME}__linked-kpi-row`}>
        <Autocomplete<RootKPIOption, false, false, true>
          options={rootKpiOptions}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.root_kpi_name)}
          // Destructure key from props — MUI provides an auto-generated key, but we override with a stable identifier
          renderOption={({ key: _key, ...props }, option) => (
            <li key={option.root_kpi_id ?? `create-${option.root_kpi_name}`} {...props}>
              {option.root_kpi_id === null ? `NEW: "${option.root_kpi_name}"` : option.root_kpi_name}
            </li>
          )}
          value={selectedRootKpi}
          inputValue={rootInputValue}
          onInputChange={(_event, newValue, reason) => {
            if (reason !== 'reset') setRootInputValue(newValue);
          }}
          onChange={handleRootKpiSelect}
          onBlur={handleRootKpiBlur}
          filterOptions={filterRootKpiOptions}
          loading={isRootKPIsLoading}
          disabled={disabled}
          size='small'
          freeSolo
          fullWidth
          autoHighlight
          isOptionEqualToValue={(option, val) => typeof val !== 'string' && option.root_kpi_id === val.root_kpi_id}
          renderInput={(params) => (
            <TextField
              {...params}
              label='Deckline Name'
              margin='dense'
              helperText={selectedRootKpi?.isNew ? '✓ New Root KPI' : undefined}
              slotProps={{
                ...params.slotProps,
                formHelperText: selectedRootKpi?.isNew
                  ? { className: `${BASE_CLASS_NAME}__helper-text-success` }
                  : undefined,
                input: {
                  ...params.slotProps.input,
                  endAdornment: isRootKPIsLoading ? (
                    <InputAdornment position='end'>
                      <CircularProgress size={14} />
                    </InputAdornment>
                  ) : (
                    params.slotProps.input.endAdornment
                  ),
                },
              }}
            />
          )}
        />
        <IconButton
          aria-label='Open KPI Library'
          disabled={disabled || selectedRootKpi?.isNew}
          className={`${BASE_CLASS_NAME}__kpi-library-dialog-button`}
          onClick={onOpenKPILibrary}
          tabIndex={-1}
        >
          <ContentPasteSearchIcon />
        </IconButton>
      </Stack>
      <Autocomplete<VariantOption, false, false, true>
        options={variantOptions}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.variant_name)}
        // Destructure key from props — MUI provides an auto-generated key, but we override with a stable identifier
        renderOption={({ key: _key, ...props }, option) => (
          <li key={option.kpi_variant_id ?? `create-${option.variant_name}`} {...props}>
            {option.kpi_variant_id === null ? `NEW: "${option.variant_name}"` : option.variant_name}
          </li>
        )}
        value={selectedVariantValue}
        inputValue={variantInputValue}
        onInputChange={(_event, newValue, reason) => {
          if (reason !== 'reset') setVariantInputValue(newValue);
        }}
        onChange={handleVariantSelect}
        onBlur={handleVariantBlur}
        filterOptions={filterVariantOptions}
        loading={isVariantsFetching}
        disabled={disabled || !localSelectedRootKpiId}
        size='small'
        freeSolo
        autoHighlight
        isOptionEqualToValue={(option, val) => typeof val !== 'string' && option.kpi_variant_id === val.kpi_variant_id}
        renderInput={(params) => (
          <TextField
            {...params}
            label='Variant'
            margin='dense'
            helperText={selectedVariantValue?.isNew ? '✓ New Variant' : undefined}
            slotProps={{
              ...params.slotProps,
              formHelperText: selectedVariantValue?.isNew
                ? { className: `${BASE_CLASS_NAME}__helper-text-success` }
                : undefined,
              input: {
                ...params.slotProps.input,
                endAdornment: isVariantsFetching ? (
                  <InputAdornment position='end'>
                    <CircularProgress size={14} />
                  </InputAdornment>
                ) : (
                  params.slotProps.input.endAdornment
                ),
              },
            }}
          />
        )}
      />
    </Box>
  );
};

export default DecklineNameInput;
